import { ColumnHeader, IColumnHeader, Table, TableHeaderRowWithExpander, TableRow, TableRowWithExpander } from '@aon/aon-tws-common-ui';
import * as React from 'react';

export interface ITableViewProps<TRowData> {
	displayOptions?: React.ComponentProps<typeof Table>;

	columns: IColumnHeader[];
	rowData: TRowData[];
	rowRenderTemplate: TRenderRowAs<TRowData>;
}

interface ITableViewState {
	rows: {
		expanded: {
			[rowKey: string]: boolean;
		};
	};
}

export class TableView<TRowData> extends React.Component<ITableViewProps<TRowData>, ITableViewState> {
	public constructor(props: ITableViewProps<TRowData>) {
		super(props);

		// -- Initialize state -- //
		this.state = {
			rows: {
				expanded: {},
			},
		};
	}

	public render() {
		return (
			<Table {...this.props.displayOptions}>
				{this.RenderColumnHeader()}
				<tbody>{this.props.rowData.map((r) => this.RenderRow(r))}</tbody>
			</Table>
		);
	}

	/// COLUMN HEADER RENDERING
	private RenderColumnHeader() {
		const { columns, rowRenderTemplate } = this.props;

		if (rowRenderTemplate.type === 'TableRowWithExpander') {
			return (
				<thead>
					<TableHeaderRowWithExpander>
						{columns.map((column) => (
							<th key={column.field}>{column.label}</th>
						))}
					</TableHeaderRowWithExpander>
				</thead>
			);
		} else {
			return <ColumnHeader columns={columns} />;
		}
	}

	// ROW RENDERING
	private RenderRow(row: TRowData) {
		const { rowRenderTemplate } = this.props;

		if (rowRenderTemplate.type === 'TableRowWithExpander') {
			const rowComponentProps = {
				key: rowRenderTemplate.rowIdentifier(row),
				onExpanderClick: () => this.ToggleRowExpansion(rowRenderTemplate.rowIdentifier(row)),
				expanded: this.state.rows.expanded[rowRenderTemplate.rowIdentifier(row)],
				expandedRowShowLeftBorder: true,
				expandedRowRender: () => rowRenderTemplate.expandedRowContent(row),
			};

			return <TableRowWithExpander {...rowComponentProps}>{this.RenderCells(row)}</TableRowWithExpander>;
		}

		if (rowRenderTemplate.type === 'TableRow') {
			return <TableRow key={rowRenderTemplate.rowIdentifier(row)}>{this.RenderCells(row)}</TableRow>;
		}
	}

	// CELL RENDERING
	private RenderCells(row: TRowData): JSX.Element[] {
		const { columns, rowRenderTemplate } = this.props;

		return columns.map((column) => {
			const renderCellForColumn = rowRenderTemplate.renderCell[column.field];

			return <td key={column.field}>{renderCellForColumn?.(row)}</td>;
		});
	}

	// ROW EXPANSION MANAGER
	private ToggleRowExpansion = (rowKey: string) => {
		const isRowExpanded = this.state.rows.expanded[rowKey];

		this.setState({
			...this.state,
			rows: {
				...this.state.rows,
				expanded: {
					...this.state.rows.expanded,
					[rowKey]: !isRowExpanded,
				},
			},
		});
	};
}

export type TRenderRowAs<TRowData> = IRenderRowAsTableRow<TRowData> | IRenderRowAsTableRowWithExpander<TRowData>;

export interface IRenderRowAsTableRow<TRowData> extends IBaseRowRenderingTemplate<TRowData> {
	type: 'TableRow';
}

export interface IRenderRowAsTableRowWithExpander<TRowData> extends IBaseRowRenderingTemplate<TRowData> {
	type: 'TableRowWithExpander';
	expandedRowContent: (rowData: TRowData) => any;
}

export interface IBaseRowRenderingTemplate<TRowData> {
	renderCell: {
		[forColumn: string]: ((forRow: TRowData) => any) | undefined;
	};
	rowIdentifier: (rowData: TRowData) => string;
}
